/**
 * LinkedCardsSection — displays linked cards grouped by link type within
 * the CardDetailModal. Each entry shows card title, number, and list name
 * as a clickable reference. Non-read-only users see Add Link and Remove buttons.
 *
 * Link types are displayed in order: Parent, Children, Related, Blocks, Blocked By.
 * Only groups with links are rendered.
 *
 * Validates: Requirements 11.1, 11.2, 11.6, 11.7
 */

import * as React from 'react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Supported link relationship types */
export type LinkType = 'parent' | 'child' | 'related' | 'blocks' | 'blocked-by';

/** Response item for a single link */
export interface LinkResponseItem {
  readonly linkId: string;
  readonly linkType: LinkType;
  readonly targetCardId: string;
  readonly targetCardTitle: string;
  readonly targetCardStatus: string;
}

export interface LinkedCardsSectionProps {
  readonly boardId: string;
  readonly cardId: string;
  readonly links: LinkResponseItem[];
  readonly isReadOnly: boolean;
  readonly availableCards?: readonly { cardId: string; cardNumber: number; title: string }[];
  readonly onLinkCreated: (link: LinkResponseItem) => void;
  readonly onLinkRemoved: (linkId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Display order and labels for link type groups (peer links only) */
const LINK_TYPE_CONFIG: readonly { type: LinkType; label: string }[] = [
  { type: 'related', label: 'Related' },
  { type: 'blocks', label: 'Blocks' },
  { type: 'blocked-by', label: 'Blocked By' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Groups links by type, preserving display order */
function groupLinksByType(
  links: readonly LinkResponseItem[],
): Map<LinkType, LinkResponseItem[]> {
  const groups = new Map<LinkType, LinkResponseItem[]>();
  for (const link of links) {
    const existing = groups.get(link.linkType);
    if (existing) {
      existing.push(link);
    } else {
      groups.set(link.linkType, [link]);
    }
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LinkedCardsSection({
  boardId,
  cardId,
  links,
  isReadOnly,
  availableCards,
  onLinkCreated,
  onLinkRemoved,
}: LinkedCardsSectionProps): React.ReactElement {
  const groupedLinks = React.useMemo(() => groupLinksByType(links), [links]);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedType, setSelectedType] = React.useState<LinkType>('related');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const hasLinks = links.length > 0;

  // Filter available cards by search query (exclude self and already-linked cards)
  const linkedCardIds = React.useMemo(() => new Set(links.map((l) => l.targetCardId)), [links]);
  const filteredCards = React.useMemo(() => {
    if (!availableCards || !searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return availableCards
      .filter((c) => c.cardId !== cardId && !linkedCardIds.has(c.cardId))
      .filter((c) => c.title.toLowerCase().includes(q) || `#${c.cardNumber}`.includes(q))
      .slice(0, 8);
  }, [availableCards, searchQuery, cardId, linkedCardIds]);

  const handleAddLink = React.useCallback(async (targetCardId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await import('../../services/api-client').then((api) =>
        api.post<{ data: { linkId: string } }>(
          `/v1/boards/${boardId}/cards/${cardId}/links`,
          { targetCardId, linkType: selectedType },
        ),
      );
      const targetCard = availableCards?.find((c) => c.cardId === targetCardId);
      onLinkCreated({
        linkId: response.data.linkId ?? crypto.randomUUID(),
        linkType: selectedType,
        targetCardId,
        targetCardTitle: targetCard?.title ?? 'Linked card',
        targetCardStatus: '',
      });
      setSearchQuery('');
      setShowAddForm(false);
    } catch {
      setError('Failed to create link');
    } finally {
      setIsSubmitting(false);
    }
  }, [boardId, cardId, selectedType, availableCards, onLinkCreated]);

  return (
    <section aria-label="Linked Cards" className="mb-4">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Linked Cards
        </h3>
        {!isReadOnly && !showAddForm && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            aria-label="Add link to another card"
            onClick={() => { setShowAddForm(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          >
            Add Link
          </Button>
        )}
      </div>

      {/* Add link inline form */}
      {showAddForm && (
        <div className="mb-3 p-2 border border-border rounded-md bg-muted/30 space-y-2">
          <div className="flex gap-2">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as LinkType)}
              className="text-xs border border-input rounded-md px-2 py-1.5 bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Link type"
            >
              <option value="related">Related</option>
              <option value="blocks">Blocks</option>
              <option value="blocked-by">Blocked By</option>
            </select>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cards by title or #number..."
              className="flex-1 text-sm border border-input rounded-md px-2 py-1.5 bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Search for a card to link"
              disabled={isSubmitting}
            />
            <button
              onClick={() => { setShowAddForm(false); setSearchQuery(''); setError(null); }}
              className="text-xs text-muted-foreground hover:text-card-foreground px-2"
              aria-label="Cancel"
            >
              ✕
            </button>
          </div>

          {/* Search results */}
          {filteredCards.length > 0 && (
            <ul className="space-y-0.5 max-h-32 overflow-y-auto" role="listbox" aria-label="Search results">
              {filteredCards.map((card) => (
                <li key={card.cardId}>
                  <button
                    onClick={() => handleAddLink(card.cardId)}
                    disabled={isSubmitting}
                    className="w-full text-left px-2 py-1 text-sm rounded hover:bg-accent focus:bg-accent focus:outline-none disabled:opacity-50"
                    role="option"
                  >
                    <span className="text-muted-foreground mr-1">#{card.cardNumber}</span>
                    {card.title}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {searchQuery.trim() && filteredCards.length === 0 && !isSubmitting && (
            <p className="text-xs text-muted-foreground px-1">No matching cards found</p>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      {/* Empty state */}
      {!hasLinks && !showAddForm && (
        <p className="text-sm text-muted-foreground italic">
          No linked cards
        </p>
      )}

      {/* Link groups */}
      {hasLinks && (
        <div className="space-y-3">
          {LINK_TYPE_CONFIG.map(({ type, label }) => {
            const groupLinks = groupedLinks.get(type);
            if (!groupLinks || groupLinks.length === 0) return null;

            return (
              <LinkGroup
                key={type}
                label={label}
                links={groupLinks}
                isReadOnly={isReadOnly}
                boardId={boardId}
                cardId={cardId}
                onLinkRemoved={onLinkRemoved}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// LinkGroup — renders a single group of links with a subheading
// ---------------------------------------------------------------------------

interface LinkGroupProps {
  readonly label: string;
  readonly links: readonly LinkResponseItem[];
  readonly isReadOnly: boolean;
  readonly boardId: string;
  readonly cardId: string;
  readonly onLinkRemoved: (linkId: string) => void;
}

function LinkGroup({
  label,
  links,
  isReadOnly,
  boardId,
  cardId,
  onLinkRemoved,
}: LinkGroupProps): React.ReactElement {
  return (
    <div>
      <h4 className="text-xs font-medium text-muted-foreground mb-1">
        {label}
      </h4>
      <ul className="space-y-1" role="list" aria-label={`${label} links`}>
        {links.map((link) => (
          <LinkEntry
            key={link.linkId}
            link={link}
            isReadOnly={isReadOnly}
            boardId={boardId}
            cardId={cardId}
            onRemove={() => onLinkRemoved(link.linkId)}
          />
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LinkEntry — a single linked card entry
// ---------------------------------------------------------------------------

interface LinkEntryProps {
  readonly link: LinkResponseItem;
  readonly isReadOnly: boolean;
  readonly boardId: string;
  readonly cardId: string;
  readonly onRemove: () => void;
}

function LinkEntry({
  link,
  isReadOnly,
  boardId,
  onRemove,
}: LinkEntryProps): React.ReactElement {
  return (
    <li
      className={cn(
        'flex items-center justify-between rounded-sm px-2 py-1.5',
        'hover:bg-accent/50 transition-colors group',
      )}
    >
      <a
        href={`/boards/${boardId}/cards/${link.targetCardId}`}
        className="flex-1 min-w-0 flex items-center gap-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring rounded-sm px-1"
        aria-label={`Navigate to card: ${link.targetCardTitle}`}
      >
        <span className="truncate font-medium text-card-foreground">
          {link.targetCardTitle}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          · {link.targetCardStatus}
        </span>
      </a>

      {!isReadOnly && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove link to ${link.targetCardTitle}`}
          className={cn(
            'ml-2 shrink-0 p-1 rounded-sm text-muted-foreground',
            'hover:text-destructive hover:bg-destructive/10',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity',
          )}
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// TrashIcon — simple inline SVG trash icon
// ---------------------------------------------------------------------------

function TrashIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5 3.25V4H2.75a.75.75 0 000 1.5h.3l.815 8.15A1.5 1.5 0 005.357 15h5.285a1.5 1.5 0 001.493-1.35l.815-8.15h.3a.75.75 0 000-1.5H11V3.25A2.25 2.25 0 008.75 1h-1.5A2.25 2.25 0 005 3.25zm2.25-.75a.75.75 0 00-.75.75V4h3V3.25a.75.75 0 00-.75-.75h-1.5zM6.05 6a.75.75 0 01.787.713l.275 5.5a.75.75 0 01-1.498.075l-.275-5.5A.75.75 0 016.05 6zm3.9 0a.75.75 0 01.712.787l-.275 5.5a.75.75 0 01-1.498-.075l.275-5.5A.75.75 0 019.95 6z"
        clipRule="evenodd"
      />
    </svg>
  );
}
